import React, { useCallback, useMemo } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import Select, { type OptionType, type OptionsType } from '@atlaskit/select';
import { AnyAri } from '@atlassian/ari/any-ari';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { matchValueToSelectOptions } from '@atlassian/jira-directory-base-v3/src/utils/search-string/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { getAriConfig } from '@atlassian/jira-platform-ari/src/index.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { FilterFields } from '@atlassian/jira-projects-directory-v3-utils/src/constants.tsx';
import type { ProjectCategoryPicker$key } from '@atlassian/jira-relay/src/__generated__/ProjectCategoryPicker.graphql';
import { useScopedFilter } from '../../../../controllers/filter-state/index.tsx';
import messages from './messages.tsx';

type Props = {
	dataRef: ProjectCategoryPicker$key;
};

export const ProjectCategoryPicker = ({ dataRef }: Props): React.JSX.Element => {
	const ariaLabelGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? messages.ariaLabelGalaxia
		: messages.ariaLabel;
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsEvent = createAnalyticsEvent({
		action: 'changed',
		actionSubject: 'refinementBar',
	});
	const [value, { updateFilter }] = useScopedFilter(FilterFields.ProjectCategoryId);

	const data = useFragment<ProjectCategoryPicker$key>(
		graphql`
			fragment ProjectCategoryPicker on JiraQuery {
				allJiraProjectCategories(cloudId: $cloudId) {
					edges {
						node {
							id
							name
						}
					}
				}
			}
		`,
		dataRef,
	);

	const onChange = useCallback(
		(newValue: OptionType | null) => {
			updateFilter({
				[FilterFields.ProjectCategoryId]: newValue ? String(newValue.value) : '',
				[FilterFields.ProjectCategoryLabel]: newValue ? String(newValue.label) : '',
			});
			fireUIAnalytics(analyticsEvent, 'ProjectCategoryPicker');
		},
		[updateFilter, analyticsEvent],
	);

	const options: OptionsType<OptionType> = useMemo(
		() =>
			data?.allJiraProjectCategories?.edges?.map((edge) => ({
				value: edge?.node?.id
					? (fg('camptasks-1102-enable-atlassian-ari-projects')
							? AnyAri.parse(edge?.node?.id)
							: getAriConfig(edge?.node?.id)
						).resourceId
					: '',
				label: edge?.node?.name || '',
			})) || [],
		[data],
	);

	const selectedValue = useMemo(() => matchValueToSelectOptions(value, options), [value, options]);

	return (
		<SelectWrapper>
			<Select
				aria-label={formatMessage(ariaLabelGalaxia)}
				placeholder={formatMessage(messages.placeholder)}
				isClearable
				name={FilterFields.ProjectCategoryId}
				value={selectedValue}
				onChange={onChange}
				options={options}
			/>
		</SelectWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SelectWrapper = styled.div({
	minWidth: '200px',
});
